import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export function unwrapJsonNetReferences<T = any>(root: T): T {
  if (root == null || typeof root !== 'object') {
    return root;
  }

  const idMap = new Map<string, any>();

  const indexObjects = (value: any): void => {
    if (value == null || typeof value !== 'object') {
      return;
    }
    if (value.$id != null && value.$ref == null) {
      idMap.set(String(value.$id), value);
    }
    if (Array.isArray(value)) {
      value.forEach(indexObjects);
      return;
    }
    if (Array.isArray(value.$values)) {
      value.$values.forEach(indexObjects);
    }
    Object.keys(value).forEach(key => {
      if (key !== '$id' && key !== '$ref') {
        indexObjects(value[key]);
      }
    });
  };

  const resolve = (value: any): any => {
    if (value == null || typeof value !== 'object') {
      return value;
    }
    if (value.$ref != null) {
      const target = idMap.get(String(value.$ref));
      if (target) {
        return resolve(cloneWithoutJsonNetMeta(target));
      }
      return value;
    }
    if (Array.isArray(value.$values)) {
      return value.$values.map(resolve);
    }
    if (Array.isArray(value)) {
      return value.map(resolve);
    }
    const out: any = {};
    Object.keys(value).forEach(key => {
      if (key === '$id' || key === '$ref') {
        return;
      }
      out[key] = resolve(value[key]);
    });
    return out;
  };

  indexObjects(root);
  const resolved = resolve(root);
  return normalizeHttpBody(resolved);
}

export function asArray<T = any>(value: any): T[] {
  if (value == null) {
    return [];
  }
  const unwrapped = unwrapJsonNetReferences(value);
  if (Array.isArray(unwrapped)) {
    return unwrapped;
  }
  return extractJsonNetArray(unwrapped);
}

export function normalizeHttpBody(body: any): any {
  if (body == null || typeof body !== 'object') {
    return body;
  }
  if (Array.isArray(body)) {
    return body.map(item => unwrapJsonNetReferences(item));
  }
  if (Array.isArray(body.$values)) {
    return body.$values;
  }

  const dataField = body.data ?? body.Data;
  if (dataField != null) {
    const normalizedData = extractJsonNetArray(dataField);
    if (body.data !== undefined) {
      body.data = normalizedData;
    }
    if (body.Data !== undefined) {
      body.Data = normalizedData;
    }
  }

  return body;
}

function cloneWithoutJsonNetMeta(value: any): any {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(cloneWithoutJsonNetMeta);
  }
  const out: any = {};
  Object.keys(value).forEach(key => {
    if (key !== '$id' && key !== '$ref') {
      out[key] = value[key];
    }
  });
  return out;
}

export function extractJsonNetArray(value: any): any[] {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'object' && Array.isArray(value.$values)) {
    return value.$values;
  }
  return [];
}

export function normalizePaginatedResponse<T = any>(raw: any): {
  pageIndex: number;
  pageSize: number;
  count: number;
  data: T[];
  total?: number;
  subTotal?: number;
  paidSum?: number;
  couponDiscount?: number;
  courierFeeTotal?: number;
  tipTotal?: number;
  refundSum?: number;
} {
  const response = unwrapJsonNetReferences(raw);
  if (!response) {
    return { pageIndex: 0, pageSize: 0, count: 0, data: [] };
  }

  return {
    pageIndex: response.pageIndex ?? response.PageIndex ?? 0,
    pageSize: response.pageSize ?? response.PageSize ?? 0,
    count: response.count ?? response.Count ?? 0,
    total: response.total ?? response.Total,
    subTotal: response.subTotal ?? response.SubTotal,
    paidSum: response.paidSum ?? response.PaidSum,
    couponDiscount: response.couponDiscount ?? response.CouponDiscount,
    courierFeeTotal: response.courierFeeTotal ?? response.CourierFeeTotal,
    tipTotal: response.tipTotal ?? response.TipTotal,
    refundSum: response.refundSum ?? response.RefundSum,
    data: extractJsonNetArray(response.data ?? response.Data)
  };
}

@Injectable({
  providedIn: 'root'
})
export class JsonNetReferenceInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (!(event instanceof HttpResponse) || event.status !== 200) {
          return event;
        }
        const body = event.body;
        if (body == null || typeof body !== 'object') {
          return event;
        }
        return event.clone({
          body: normalizeHttpBody(unwrapJsonNetReferences(body))
        });
      })
    );
  }
}
